/**
 * CourseMap — the Mapbox course map: the event's GPX route with every located
 * camera pinned as the shared {@link CameraSphere}. The sphere IS the live
 * video for an online camera (the surface's `renderVideo`, masked into a
 * circle), not a still thumbnail. Everything arrives as props, so the old
 * viewer's RouteMap and the watch screen draw the same map.
 *
 * Interaction (shared camera sphere):
 *   1. Hover, keyboard focus or a first tap previews the camera above its
 *      fix. Previewing never changes selection or moves the map.
 *   2. A click, Enter or (on touch) a second tap opens the camera: `onWatch`
 *      with the sphere's frame, by then its preview, so the surface's player
 *      can grow out of it (the watch screen's canvas does), and the sphere
 *      tucks back into its circle. The map keeps no view of the camera of its
 *      own: the surface's player is the player.
 *   Two states on every surface (the founder's rule): no first tap opens.
 *   With `presentation="card"` (the swap card) the map draws a light picture
 *   of the course instead (CourseCard: the whole course as a line on the
 *   canvas tone, each located camera a small dot, no tiles), over the Mapbox
 *   map kept mounted underneath (the surface's stylesheet hides it), so going
 *   back is instant and keeps the viewer's basemap.
 *   With `interactive={false}` (the swap card) the map is a still picture: no
 *   controls, no gestures, cameras don't respond, and it is hidden from
 *   assistive tech. It follows the selection under the same rule, except
 *   while `holdView` holds it still. The map resizes with its container (once
 *   a frame, only when its size changes), and opens afresh whenever it moves
 *   between the card and the canvas (`interactive` flips) or is shown again
 *   (`shown`), once the map is ready. A camera that takes focus under what
 *   covers the map (`coveredBy`) is panned clear of it.
 *
 * The play world (VITE_PLAY_WORLD=true, an event with a course): the event's course as a
 * play world (CourseMapWorld, the v2 terrain) over the Mapbox map, which stays mounted
 * underneath (hidden, `data-world`) so the card keeps working and going back is instant. While the world builds the Mapbox map shows, saying how far along it is; a
 * world that can't show leaves the map on satellite and its World switch says why. In the swap
 * card a ready world is the picture itself (in place of CourseCard's line): one still frame of
 * the whole course from its overview, its cameras as dots. The world draws every frame only
 * while it is the full map on screen (not in the swap card or behind another tab) and hears its
 * keys only then, while interactive. The Mapbox map draws only while it shows too: under the world,
 * the swap card's picture or another tab it holds its frames once its style has loaded, and
 * draws afresh the moment it shows again. A world pin with live video is a sphere too: it
 * previews on the first tap and opens on the second, the same way as a sphere here: `onWatch`
 * with its frame (its preview's).
 *
 * Following a host's map (`hostView`, docs/features/follow-host-map.md): the live map on screen, the world or the Mapbox
 * map, eases to the host's view while the viewer follows, on the basemap they picked, and a live host's offers theirs. On
 * the Mapbox map a frame loop eases its camera (`followOnMapbox`), holding the map's own moves meanwhile (the opening
 * frame, the follow rule, focus panned clear of a cover); the viewer's gesture, a fit, Recenter, the 2D/3D switch or an
 * athlete search lets go. The page's control for it sits at the top centre of either.
 *
 * Several courses (`routes`, two or more): each is drawn in its own colour (the course palette, in the payload's order)
 * on every basemap and the swap card's picture, the shortest on top, the map opens on them all, and a small key names them
 * (CourseLegend, bottom left, while the map is interactive). One course keeps the map's own style.
 *
 * Opening (first load, a flip, shown again): the map frames every live camera
 * (online, with the viewer's connection up) together with the course, so none
 * is off screen; with no course and no live camera, the located cameras. A
 * side a camera sets keeps room for its sphere, and the frame keeps clear of
 * what covers the map's foot (`coveredBy`) when the map can spare it; fits keep
 * the viewer's tilt and bearing. That opening frame holds, taking in a camera
 * that goes live meanwhile (the room connecting after the map loaded), until
 * the viewer moves the map (a gesture, Zoom to course route, Recenter) or the
 * follow rule moves it to a newly watched camera. `holdView` holds it still.
 *
 * Visual contract (Play system 02), owned by the camera sphere:
 *   - The tail stays anchored to the camera's actual coordinate
 *   - Green rim dot only for live video (a camera online while the viewer's
 *     own connection is up, never while it reconnects); pink ring for the
 *     viewer's selection; a star for the called (featured) camera
 *   - Offline cameras stay on the map but cannot preview or open, and never
 *     show a picture
 *
 * Performance contract for any map-bearing primitive
 * (see docs/features/v1/PRIMITIVES_CATALOG.md §"Map primitive perf contract"
 * for the wider checklist Director should follow):
 *   - GPX is fetched from the stable public route endpoint when a route id is
 *     present. Do not force-cache Rails ActiveStorage redirects: the browser can
 *     replay an expired signed disk URL. Route content caching is controlled by
 *     response headers from the API endpoint or nginx static route asset path.
 *   - Mapbox + API origins are preconnected at the document level
 *     (frontend/web/index.html). New map primitives must NOT introduce a
 *     new tile/style origin without also adding the preconnect.
 *   - The map mounts in two phases: initial bounds from `route.bounds`
 *     paint before the GPX arrives, then the parsed line is added once
 *     `styleReady && parsed`. Never block first paint on the GPX fetch.
 */

import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FocusEvent as ReactFocusEvent, type ReactNode } from 'react'
import MapGL, { AttributionControl, Marker, type MapRef } from 'react-map-gl/mapbox-legacy'
import { useReducedMotion } from 'motion/react'
import { LngLatBounds } from 'mapbox-gl'
import { MountainSnow } from 'lucide-react'
import {
  CameraSphere,
  cameraFrame,
  formatAge,
  useExpandedCamera,
  type CameraFreshness,
  type ChangeCameraMode,
} from '@/components/map/camera-sphere'
import { parseGpx, type ParsedGpx } from '@/lib/comrades/gpx'
import { routeGpxCacheMode, routeGpxUrlFor, routeWorldUrlFor } from '@/lib/map/routeGpx'
import { isValidRouteBounds, shouldAutoFocusWatchedCamera, type RouteBounds } from '@/lib/map/routeFocus'
import { holdRepaints } from '@/lib/map/holdRepaints'
import type { HostViewSource, HostViewStore } from '@/lib/map/hostView'
import { followOnMapbox, readMapboxView } from '@/lib/map/mapboxHostView'
import { fadeRouteOverlay, syncRouteOverlay, type RouteOverlayData } from '@/lib/map/routeOverlay'
import { athleteAt, syncAthleteOverlay, type AthleteOverlayData } from '@/lib/map/athleteOverlay'
import { playTokens } from '@/lib/theme/playTokens'
import type { EventAthletes } from '@/lib/watch/eventAthletes'
import { matchesAthlete, withinArea } from '@/lib/watch/athleteSearch'
import { cn } from '@/lib/utils'
import { AthleteCard } from './AthleteCard'
import { AthleteSearch, type AthleteSearchHit } from './AthleteSearch'
import { CourseCard, type CourseCardCourse, type CourseCardDot } from './CourseCard'
import { CourseLegend } from './CourseLegend'
import { CourseMapControls } from './CourseMapControls'
import { courseColor, drawnCourses, type CourseMapRoute, type DrawnCourse } from './courses'
import { locatedCameras, type CourseMapCoordinate, type Located } from './locatedCameras'
import { SphereInitials } from './SphereInitials'
import { useCourseWorld, type WorldCourse } from './useCourseWorld'
import type { WorldElement } from '@/lib/comrades/worldLayer'
import type { GeneratedTerrain } from '@/lib/terrain-generator/generate'
import type { GpxRoute } from '@/lib/terrain-generator/route'
import 'mapbox-gl/dist/mapbox-gl.css'
import './course-map.css'

/** `world` is the event's play world (docs/features/play-world-events.md); the others are Mapbox styles. */
export type CourseMapBasemap = 'world' | 'standard' | 'satellite'

/** The Mapbox styles: what the Mapbox map itself draws. */
export type MapboxBasemap = Exclude<CourseMapBasemap, 'world'>

/** The full map, or the light card picture of the course (see `presentation`). */
export type CourseMapPresentation = 'map' | 'card'

/** A corner for the Mapbox wordmark (Mapbox's `logoPosition`). */
export type CourseMapLogoPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

/** A tracked athlete at their newest fix, as both basemaps draw them: in their race's colour, stale or fresh. */
export interface CourseMapAthlete {
  id: string
  name: string
  bib: string | null
  latitude: number
  longitude: number
  color: string
  /** Their race's course (its route id), or null without one: the world puts its kilometre marks there while they're picked. */
  courseKey: string | null
  stale: boolean
}

export interface CourseMapCamera {
  identity: string
  name: string
  /** Null, or out of range, keeps the camera off the map. */
  coordinate: CourseMapCoordinate | null
  /**
   * Video is available now (the surface decides how it knows). Offline
   * cameras never get a picture.
   */
  online: boolean
  /** Short state while not online ("Reconnecting"); "Offline" when left out. */
  status?: string
  freshness?: CameraFreshness
}

export interface CourseMapProps {
  eventId: string
  /**
   * The event's primary course: the one the world loads, framed until the others' lines arrive. `world_url` is its
   * server-built pack (the last one built, also while the next builds); `world_status` "building" without one waits for
   * the server.
   */
  route: {
    id?: string | null
    gpx_url?: string | null
    bounds?: RouteBounds | null
    world_url?: string | null
    world_status?: string | null
  } | null
  /**
   * Every course of the event (the viewer payload's `routes`, `route` among them). With two or more, each is drawn in
   * its own colour with a legend (see the component's notes). Left out, or with one, `route` is the course.
   */
  routes?: CourseMapRoute[] | null
  /**
   * Every camera; unlocated ones are left off. Keep the array stable between
   * renders (memoise it): a new array re-runs the follow-selection ease.
   */
  cameras: CourseMapCamera[]
  selectedIdentity: string | null
  /** The host's call (the old viewer: the producer's featured camera), the sphere's star. */
  calledIdentity?: string | null
  /**
   * The follow rule. When true, the map eases to the selected camera whenever
   * the selection (or the camera list) changes. When false, it eases only if
   * there is no course to frame. While the map still shows its opening frame
   * (every live camera, see the component's notes), only the selection moving
   * from one camera to another follows.
   */
  followSelection?: boolean
  /**
   * A camera was opened. `frame` is its sphere's preview (or circle, opened with a click before the hover preview
   * grew) as it opened, null if it can't be found, for the surface's player to grow out of.
   */
  onWatch: (identity: string, frame: Element | null) => void
  /** false for the swap card: no controls, no camera interaction. */
  interactive?: boolean
  /**
   * The play world's camera controls (Fly, the overview, the keyboard's help) show: true when left out. The watch
   * screen's phone Map tab turns them off outside full screen, where the panel is small: just its full-screen toggle and
   * the basemap switch stay. Off, they aren't rendered (nothing to focus), and a flight under way ends.
   */
  worldControls?: boolean
  /**
   * `card`: a light picture of the course for a small card (the watch screen's swap card), drawn without Mapbox: the
   * whole course framed as a plain line on the canvas tone (the located cameras without a course), and each located
   * camera as a small dot, green only while it is live (online with the viewer's connection up), grey otherwise, with
   * a pink ring on the selected one. No tiles, spheres, wordmark or controls show; the Mapbox map stays mounted
   * underneath (the surface's stylesheet hides it, `data-presentation="card"`), so switching back never remounts it
   * and keeps the viewer's basemap. Pair it with `interactive={false}`. `map` (the default) is the full map.
   */
  presentation?: CourseMapPresentation
  /**
   * The map is on screen (true when left out). A surface that keeps it mounted out of sight (the watch screen's phone
   * Map tab behind another tab, or its landscape layout) says so: the map holds its drawing meanwhile and opens afresh
   * when it shows again.
   */
  shown?: boolean
  /**
   * Told whether the map has something drawn to show: the play world, or the Mapbox map once its style has loaded while
   * no world is on its way (a built world downloading, or having its scene built, counts as on its way; one the server is
   * still building doesn't,
   * as the Mapbox map stands in for it). True without a Mapbox token, where "Map unavailable" shows instead. The watch
   * screen holds a loading wait over the full-size map until then.
   */
  onLoadedChange?: (loaded: boolean) => void
  /**
   * The viewer's own connection to the video while it isn't up, in a few words ("Tuning back in…"). A camera's picture
   * may be a frozen frame meanwhile and nothing can be known about a camera, so none shows as live: every sphere loses
   * its green rim and says these words. Left out, the connection is up.
   */
  connectionStatus?: string
  /**
   * The map stays where it is while true: the follow rule doesn't move it (the watch screen holds it while a camera
   * opened on it grows out of its sphere and fills the canvas). A selection made meanwhile isn't caught up on after.
   */
  holdView?: boolean
  /**
   * Something floating over the map (the watch screen's swap card): a camera that takes focus under it is panned clear
   * (upward when it sits in the map's lower half: a preview grows up from the fix), so focus is never hidden under it,
   * and the opening frame keeps clear of it when it sits at the map's foot.
   */
  coveredBy?: () => Element | null
  /**
   * An online camera's surface inside its sphere: return an element. It is
   * called inline while rendering, and only for online cameras, so don't call
   * hooks in it (return a component that does). `fallback` is the camera's
   * initials, for while the video is still arriving.
   */
  renderVideo: (camera: CourseMapCamera, fallback: ReactNode) => ReactNode
  /** The configured basemap; the viewer can still switch. The world when it is on (VITE_PLAY_WORLD), else satellite. */
  basemap?: CourseMapBasemap
  /**
   * Where the Mapbox wordmark sits, read once when the map is created. Mapbox's
   * default (bottom left) when left out. Mapbox's terms require the wordmark,
   * so move it clear of overlays rather than hiding it.
   */
  logoPosition?: CourseMapLogoPosition
  /**
   * `compact`: Mapbox's attribution as its compact (i) control, in the
   * wordmark's corner. Mapbox's terms ask for it beside the wordmark. Left
   * out, the map shows no attribution control (the old viewer's maps).
   */
  attribution?: 'compact'
  /**
   * The event's world layer (`useWorldLayer`'s elements): sponsors, course points and hotspots the play world draws as
   * markers with cards, those on `route` or at a point in its area. The Mapbox map draws none yet. Keep the array
   * stable between renders (memoise it).
   */
  worldElements?: readonly WorldElement[]
  /**
   * The event's tracked athletes (docs/features/athletes-and-trackers.md, Map): each with a position is a dot in its
   * race's course colour under the camera spheres, hollow once stale; a tap opens their card. On the Mapbox map only.
   */
  athletes?: EventAthletes | null
  /**
   * Following a host's map (docs/features/follow-host-map.md), on whichever basemap the viewer has: while `follow.on`,
   * the live map on screen (the world, or the Mapbox map) eases to a host's view, and a live host's offers theirs to
   * `source`. The viewer's own move on it lets go (`follow.onBreak`). `overlay` (the page's Follow control, or a host's
   * "live to your audience" tag) sits at the top centre of the interactive map.
   */
  hostView?: CourseMapHostView | null
  /**
   * Show this athlete (docs/features/badger-cohost.md, Show on map): the play world (when the event has one), flying to
   * their dot with their card open, as picking them in the search does. Each `key` shows once, as soon as the world is
   * on screen and they have a position there; one that can't be shown within `ATHLETE_FOCUS_WAIT_MS` is dropped.
   */
  athleteFocus?: { id: string; key: number } | null
  /** Extra markers (the old viewer's tracked entities). */
  children?: ReactNode
  className?: string
}

const STYLE_STANDARD = 'mapbox://styles/mapbox/standard'
const STYLE_SATELLITE = 'mapbox://styles/mapbox/standard-satellite'

/** Every gesture handler off: the swap card's map is a picture, moved only by code. */
const STILL_MAP = {
  boxZoom: false,
  doubleClickZoom: false,
  dragPan: false,
  dragRotate: false,
  keyboard: false,
  scrollZoom: false,
  touchPitch: false,
  touchZoomRotate: false,
} as const

/** How far clear of what covers the map (`coveredBy`) a focused camera is panned. */
const COVER_CLEARANCE = 16

/** A lone camera's zoom: a box with no area would zoom all the way in. */
const LONE_CAMERA_ZOOM = 11.5
/** An athlete search frames this many finds at most; more leave the view be. */
const SEARCH_FRAMES_UP_TO = 20
/** How close a search brings one athlete, unless the view is closer already. */
const SEARCH_ZOOM = 14
/** How long the typing pauses before the view moves to a search's finds. */
const SEARCH_SETTLE_MS = 350
/** How many athletes a search lists; it says how many more match. */
const SEARCH_LISTS_UP_TO = 20
/** How long a shown athlete (`athleteFocus`) waits for the world and their dot before it is dropped. */
export const ATHLETE_FOCUS_WAIT_MS = 15_000

type Padding = { top: number; right: number; bottom: number; left: number }

/** Room around the course: the controls above, the spheres' tails below. */
const COURSE_PADDING: Padding = { top: 64, right: 32, bottom: 96, left: 32 }

/**
 * Room on a side of the opening frame that a camera sets: its sphere stands
 * about 80px above its fix, its name under the circle up to about 110px wide.
 */
const CAMERA_ROOM: Padding = { top: 96, right: 64, bottom: 96, left: 64 }

/**
 * `room` for this map's size. Mapbox's fit does nothing when the padding
 * leaves no room, so a map that can't spare it (it would take over half an
 * axis, as in the swap card) gives each side of that axis a tenth of it
 * instead. A full-size map keeps the full padding.
 */
function paddingFor(map: MapRef, room: Padding) {
  const { clientWidth: width, clientHeight: height } = map.getContainer()
  const spareX = room.left + room.right <= width / 2
  const spareY = room.top + room.bottom <= height / 2
  const tenth = (size: number) => Math.round(size / 10)
  return {
    top: spareY ? room.top : tenth(height),
    right: spareX ? room.right : tenth(width),
    bottom: spareY ? room.bottom : tenth(height),
    left: spareX ? room.left : tenth(width),
  }
}

const coursePadding = (map: MapRef) => paddingFor(map, COURSE_PADDING)

/**
 * Room around the opening frame: the course's, a camera's on each side a
 * camera sets (every side without a course; with one, those it reaches past),
 * and clear of what covers the map's foot (`cover`, the swap card) while the
 * map can spare that too.
 */
function openingPadding(map: MapRef, box: RouteBounds, course: RouteBounds | null, cover: number) {
  const setByCamera: Record<keyof Padding, boolean> = course
    ? { top: box.north > course.north, right: box.east > course.east, bottom: box.south < course.south, left: box.west < course.west }
    : { top: true, right: true, bottom: true, left: true }
  const room = { ...COURSE_PADDING }
  for (const side of Object.keys(room) as (keyof Padding)[]) {
    if (setByCamera[side]) room[side] = Math.max(room[side], CAMERA_ROOM[side])
  }
  const covered = { ...room, bottom: Math.max(room.bottom, cover) }
  return paddingFor(map, covered.top + covered.bottom <= map.getContainer().clientHeight / 2 ? covered : room)
}

/** The box around `places`, grown from `start` (a course's); null with neither. */
function boxAround(places: readonly CourseMapCoordinate[], start: RouteBounds | null = null): RouteBounds | null {
  return places.reduce<RouteBounds | null>(
    (box, { longitude, latitude }) =>
      box
        ? {
            west: Math.min(box.west, longitude),
            south: Math.min(box.south, latitude),
            east: Math.max(box.east, longitude),
            north: Math.max(box.north, latitude),
          }
        : { west: longitude, south: latitude, east: longitude, north: latitude },
    start,
  )
}

/** The box around every one of `boxes` (null ones left out); null with none. */
function joinBoxes(boxes: readonly (RouteBounds | null)[]): RouteBounds | null {
  return boxes.reduce<RouteBounds | null>(
    (union, box) =>
      !box
        ? union
        : !union
          ? box
          : {
              west: Math.min(union.west, box.west),
              south: Math.min(union.south, box.south),
              east: Math.max(union.east, box.east),
              north: Math.max(union.north, box.north),
            },
    null,
  )
}

/** A course's box: its line's once the GPX is in, the payload's bounds until then. */
function lineBox(line: ParsedGpx | null, bounds: RouteBounds | null | undefined): RouteBounds | null {
  if (line?.points.length) return boxAround(line.points)
  return isValidRouteBounds(bounds) ? bounds : null
}

const lngLatBounds = (box: RouteBounds) => new LngLatBounds([box.west, box.south], [box.east, box.north])

/**
 * A move the viewer made on the map: Mapbox passes the gesture's DOM event
 * (pointer, wheel, touch or key). A move by code passes none, and a window
 * resize its own resize event, which isn't the viewer's say on the view.
 */
const byViewer = (event: Event | undefined) =>
  event instanceof MouseEvent || event instanceof KeyboardEvent || (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent)

/** Pick a Mapbox `lightPreset` from local clock — keeps day/dusk/dawn/night
 *  in sync with whatever the viewer's wall time is.
 *  Mapbox Standard supports: 'dawn' | 'day' | 'dusk' | 'night'. */
function lightPresetForHour(hour: number): 'dawn' | 'day' | 'dusk' | 'night' {
  if (hour >= 5 && hour < 9) return 'dawn'
  if (hour >= 9 && hour < 17) return 'day'
  if (hour >= 17 && hour < 20) return 'dusk'
  return 'night'
}

/** CourseMap's `hostView`. */
export interface CourseMapHostView {
  follow?: { store: HostViewStore; on: boolean; onBreak(): void; athlete?: string | null } | null
  source?: HostViewSource | null
  overlay?: ReactNode
}

/** A ready world drawn over the Mapbox map, with how it hands back. */
interface CourseMapWorldLayer {
  terrain: GeneratedTerrain
  gpx: GpxRoute
  /** Every course of an event with several, in their colours; null for one. */
  courses: WorldCourse[] | null
  onUnavailable: (reason: string) => void
}

/** The Mapbox map's extras from CourseMap: the world over it, its World switch, how its build is going, and why it can't show. */
interface MapboxCourseMapProps extends Omit<CourseMapProps, 'basemap'> {
  basemap?: MapboxBasemap
  /** The viewer picked a basemap (the world's Satellite switch included), so a world finishing its build doesn't take over. */
  onBasemapChange?: (basemap: CourseMapBasemap) => void
  onSelectWorld?: () => void
  /** Share of the world built (0–1), or null before the first tiles, while the map waits for it. */
  worldBuilding?: number | null
  /** A built world is downloading: the Mapbox map isn't what the viewer will see, so it doesn't count as loaded. */
  worldDownloading?: boolean
  worldProblem?: string | null
  /** The ready world, drawn over this map. */
  world?: CourseMapWorldLayer | null
  /** Every course of an event with several, each in its colour; null for one course. */
  courses?: DrawnCourse[] | null
}

const CourseMapWorld = lazy(() => import('./CourseMapWorld'))

/** The play world shows with VITE_PLAY_WORLD=true. */
function playWorldEnabled(): boolean {
  return import.meta.env.VITE_PLAY_WORLD === 'true'
}

/**
 * The one course map: the event's play world when it is on and the course has one, over the
 * Mapbox map, which stands in while the world is built and takes over whenever the viewer
 * picks a Mapbox basemap or the world can't show (see the component's notes).
 */
export function CourseMap({ basemap: configuredBasemap, routes, ...props }: CourseMapProps) {
  const worldEnabled = playWorldEnabled() && Boolean(props.route)
  // The courses, unchanged while the payload's list says the same (a refreshed payload brings a new array).
  const routesKey = JSON.stringify(routes?.map((each) => [each.id, each.name, each.distance_km, each.gpx_url, each.world_url, each.bounds]) ?? null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const courses = useMemo(() => drawnCourses(routes), [routesKey])
  const [choice, setChoice] = useState<CourseMapBasemap>(configuredBasemap ?? (worldEnabled ? 'world' : 'satellite'))
  useEffect(() => {
    if (configuredBasemap) setChoice(configuredBasemap)
  }, [configuredBasemap])

  // Showing an athlete on the map goes to the world, where it flies to them with their card open.
  const focusKey = props.athleteFocus?.key
  useEffect(() => {
    if (focusKey !== undefined && worldEnabled) setChoice('world')
  }, [focusKey, worldEnabled])

  const world = useCourseWorld({ eventId: props.eventId, route: props.route, courses, enabled: worldEnabled })
  const courseKey = `${props.eventId}|${props.route?.id ?? ''}`
  // A world that was built but stopped drawing, for this course.
  const [crashed, setCrashed] = useState<{ course: string; reason: string } | null>(null)
  const onUnavailable = useCallback((reason: string) => setCrashed({ course: courseKey, reason }), [courseKey])
  const problem = crashed?.course === courseKey ? crashed.reason : world.status === 'unavailable' ? world.reason : null
  const worldChosen = worldEnabled && choice === 'world' && !problem

  return (
    <MapboxCourseMap
      {...props}
      basemap={choice === 'world' ? 'satellite' : choice}
      onBasemapChange={setChoice}
      onSelectWorld={worldEnabled ? () => setChoice('world') : undefined}
      worldBuilding={worldChosen && world.status === 'building' ? world.share : undefined}
      worldDownloading={worldChosen && world.status === 'building' && routeWorldUrlFor({ id: props.eventId, route: props.route }) !== null}
      worldProblem={worldEnabled ? problem : null}
      world={worldChosen && world.status === 'ready' ? { terrain: world.terrain, gpx: world.gpx, courses: world.courses, onUnavailable } : null}
      courses={courses}
    />
  )
}

function MapboxCourseMap({
  eventId,
  route,
  cameras,
  selectedIdentity,
  calledIdentity = null,
  followSelection = false,
  onWatch,
  interactive = true,
  worldControls = true,
  presentation = 'map',
  shown = true,
  connectionStatus,
  holdView = false,
  coveredBy,
  renderVideo,
  basemap: configuredBasemap,
  logoPosition,
  attribution,
  worldElements,
  athletes = null,
  children,
  className,
  onBasemapChange,
  onSelectWorld,
  worldBuilding,
  worldDownloading = false,
  worldProblem = null,
  world = null,
  courses = null,
  onLoadedChange,
  hostView = null,
  athleteFocus = null,
}: MapboxCourseMapProps) {
  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined

  // Basemap + dimensionality state — both persistent in-session. Satellite
  // is the viewer default; an explicitly configured basemap still wins.
  const [basemap, setBasemap] = useState<MapboxBasemap>(configuredBasemap ?? 'satellite')
  const [is3D, setIs3D] = useState<boolean>(true)
  const lightPreset = useMemo(() => lightPresetForHour(new Date().getHours()), [])
  const mapStyle = basemap === 'satellite' ? STYLE_SATELLITE : STYLE_STANDARD

  useEffect(() => {
    if (configuredBasemap) setBasemap(configuredBasemap)
  }, [configuredBasemap])

  const [mapReady, setMapReady] = useState(false)
  const [mapRevision, setMapRevision] = useState(0)
  const [routeOverlayReady, setRouteOverlayReady] = useState(false)
  useEffect(() => {
    setMapReady(false)
    setRouteOverlayReady(false)
  }, [mapStyle])

  const mapRef = useRef<MapRef>(null)

  // Mapbox resizes with the window only. The watch screen's swap card moves
  // this map between a card and the full canvas, so it follows its own
  // container's size as well: at most once a frame, and only when the size
  // has changed (the observer's first report is the size Mapbox started at).
  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const root = rootRef.current
    if (!root || typeof ResizeObserver === 'undefined') return
    let size = { width: root.clientWidth, height: root.clientHeight }
    let frame = 0
    const observer = new ResizeObserver((entries) => {
      const box = entries[entries.length - 1]?.contentRect
      if (!box) return
      const next = { width: Math.round(box.width), height: Math.round(box.height) }
      if (next.width === size.width && next.height === size.height) return
      size = next
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => mapRef.current?.resize())
    })
    observer.observe(root)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [])

  // Apply the time-of-day lightPreset once per loaded style. Mapbox resets
  // config on style swap (outdoors ↔ satellite), so we re-apply after each
  // swap — but ONLY once per `mapStyle` value. We used to bind this to
  // every `onStyleData` event, which produced an infinite styledata →
  // setConfigProperty → styledata loop, kept `isStyleLoaded()` false
  // forever, and silently blocked the route Source from ever mounting
  // (commit before this comment was the fix; see #150-ish bug repro).
  //
  // Wrapped in try/catch because older styles ignore basemap config.
  const lightPresetSyncedFor = useRef<string | null>(null)
  useEffect(() => {
    lightPresetSyncedFor.current = null
  }, [mapStyle])
  const applyLightPreset = useCallback(() => {
    if (lightPresetSyncedFor.current === mapStyle) return
    const map = mapRef.current?.getMap()
    if (!map) return
    try {
      map.setConfigProperty('basemap', 'lightPreset', lightPreset)
      lightPresetSyncedFor.current = mapStyle
    } catch {
      // Older styles don't expose basemap config — no-op.
    }
  }, [lightPreset, mapStyle])

  const handleMapLoad = useCallback(() => {
    applyLightPreset()
    setMapReady(true)
    setMapRevision((revision) => revision + 1)
  }, [applyLightPreset])

  const handleMapIdle = useCallback(() => {
    applyLightPreset()
    setMapReady(true)
    setMapRevision((revision) => revision + 1)
  }, [applyLightPreset])

  // Animate camera pitch between 2D / 3D — only on user toggle, not on mount.
  const isFirstPitch = useRef(true)
  useEffect(() => {
    if (isFirstPitch.current) {
      isFirstPitch.current = false
      return
    }
    const map = mapRef.current?.getMap()
    if (!map) return
    try {
      map.easeTo({ pitch: is3D ? 55 : 0, duration: 500, essential: false })
    } catch {
      // map mid-transition — pitch will be picked up next interaction.
    }
  }, [is3D])

  const gpxUrl = routeGpxUrlFor({ id: eventId, route })

  // The course on show. Another event or route starts over: its line is
  // fetched and framed afresh, never drawn or framed with the last one's.
  const courseKey = `${eventId}|${gpxUrl ?? ''}`
  const [fetchedCourse, setFetchedCourse] = useState<{ key: string; gpx: ParsedGpx } | null>(null)
  const parsed = fetchedCourse?.key === courseKey ? fetchedCourse.gpx : null

  useEffect(() => {
    if (!gpxUrl) return
    let cancelled = false
    fetch(gpxUrl, { cache: routeGpxCacheMode(gpxUrl) })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`Route fetch ${res.status}`))))
      .then((text) => {
        if (cancelled) return
        setFetchedCourse({ key: courseKey, gpx: parseGpx(text) })
      })
      .catch(() => {
        // Map degrades gracefully — bubbles still render against a blank tile.
      })
    return () => {
      cancelled = true
    }
  }, [courseKey, gpxUrl])

  // The other courses' lines (an event with several), fetched as the primary's is, by their GPX link.
  const otherUrls = useMemo(
    () =>
      (courses ?? [])
        .map((course) => routeGpxUrlFor({ id: eventId, route: course.route }))
        .filter((url): url is string => Boolean(url) && url !== gpxUrl),
    [courses, eventId, gpxUrl],
  )
  const otherUrlsKey = otherUrls.join('\n')
  const [otherLines, setOtherLines] = useState<Record<string, ParsedGpx>>({})
  useEffect(() => {
    let cancelled = false
    for (const url of otherUrls) {
      fetch(url, { cache: routeGpxCacheMode(url) })
        .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`Route fetch ${res.status}`))))
        .then((text) => {
          if (!cancelled) setOtherLines((lines) => ({ ...lines, [url]: parseGpx(text) }))
        })
        .catch(() => {
          // A course whose line won't load is left off; the others still show.
        })
    }
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherUrlsKey])

  // Each course of an event with several, with its line once it has arrived (null for one course).
  const courseLines = useMemo(
    () =>
      courses?.map((course) => {
        const url = routeGpxUrlFor({ id: eventId, route: course.route })
        const line = url === gpxUrl ? parsed : url ? (otherLines[url] ?? null) : null
        return { course, line, order: course.order }
      }) ?? null,
    [courses, eventId, gpxUrl, otherLines, parsed],
  )

  // What the overlay draws: one course's line, or each of several in its colour.
  const overlay = useMemo<RouteOverlayData | null>(() => {
    if (!courseLines) return parsed?.lineFeature ?? null
    return {
      type: 'FeatureCollection',
      features: courseLines.flatMap(({ course, line, order }) =>
        line?.lineFeature ? [{ ...line.lineFeature, properties: { color: course.color, order } }] : [],
      ),
    }
  }, [courseLines, parsed])

  useEffect(() => {
    if (!mapReady) return
    const map = mapRef.current?.getMap()
    if (!map) return
    const synced = syncRouteOverlay(map, overlay)
    setRouteOverlayReady(synced)
    if (synced || !overlay) return

    const timeout = window.setTimeout(() => {
      setMapRevision((revision) => revision + 1)
    }, 250)
    return () => window.clearTimeout(timeout)
  }, [mapReady, mapRevision, mapStyle, overlay])

  // Tracked athletes: one dot each in its race's colour. With one course (or none) every dot takes the first course
  // colour, the Play yellow; with several, an athlete with no race is white.
  const [pickedAthlete, setPickedAthlete] = useState<string | null>(null)
  // The athlete under the pointer, named over their dot (a pointer that hovers; touch has the card).
  const [hoveredAthlete, setHoveredAthlete] = useState<string | null>(null)
  const closeAthlete = useCallback(() => setPickedAthlete(null), [])
  const raceOf = useCallback(
    (routeId: string | null) => {
      if (!courses) return { name: null, color: courseColor(0) }
      const course = routeId ? courses.find((each) => each.route.id === routeId) : undefined
      return course ? { name: course.name, color: course.color } : { name: null, color: playTokens.color.canvas }
    },
    [courses],
  )
  // A new fix glides the dot there (the world's do too), unless the viewer asks for less motion.
  const reducedMotion = useReducedMotion() === true
  // Each athlete with a position, for both basemaps.
  const trackedAthletes = useMemo<CourseMapAthlete[]>(() => {
    if (!athletes || athletes.positions.size === 0) return []
    return athletes.roster.flatMap((athlete) => {
      const position = athletes.positions.get(athlete.id)
      if (!position) return []
      return [
        {
          id: athlete.id,
          name: athlete.name,
          bib: athlete.bib,
          latitude: position.latitude,
          longitude: position.longitude,
          color: raceOf(athlete.routeId).color,
          courseKey: athlete.routeId,
          stale: athletes.at - position.recordedAt > athletes.staleAfterMs,
        },
      ]
    })
  }, [athletes, raceOf])
  // The viewer's athlete search (AthleteSearch): what they typed, or null while it is closed. `spotlight` is the
  // athletes on the map it found, null until it finds one: they wear the selection ring while the rest of the dots,
  // and on the Mapbox map the course, fade back.
  const [athleteQuery, setAthleteQuery] = useState<string | null>(null)
  // What a search can find: the athletes this map draws, which on the play world is only those inside its area.
  const findable = useMemo(() => withinArea(trackedAthletes, world?.terrain.bounds ?? null), [trackedAthletes, world])
  const spotlight = useMemo(() => {
    if (!athleteQuery?.trim()) return null
    const found = new Set(findable.filter((athlete) => matchesAthlete(athlete, athleteQuery)).map((athlete) => athlete.id))
    return found.size > 0 ? found : null
  }, [findable, athleteQuery])
  // The search's list: everyone who matches, those the map draws first (in roster order) and then those it doesn't (no
  // position yet, or one outside the play world's area), the first SEARCH_LISTS_UP_TO of them.
  const searchList = useMemo(() => {
    const query = athleteQuery?.trim()
    if (!query || !athletes) return { hits: [] as AthleteSearchHit[], total: 0, onMap: 0 }
    const drawn = new Set(findable.map((athlete) => athlete.id))
    const matches = athletes.roster.filter((athlete) => matchesAthlete(athlete, query))
    const onMap = matches.filter((athlete) => drawn.has(athlete.id))
    const listed = [...onMap, ...matches.filter((athlete) => !drawn.has(athlete.id))].slice(0, SEARCH_LISTS_UP_TO)
    const hits = listed.map<AthleteSearchHit>((athlete) => {
      const position = drawn.has(athlete.id) ? athletes.positions.get(athlete.id) : undefined
      const race = raceOf(athlete.routeId)
      return {
        id: athlete.id,
        name: athlete.name,
        bib: athlete.bib,
        photoUrl: athlete.photoUrl,
        race: race.name ? { name: race.name, color: race.color } : null,
        age: position ? formatAge(athletes.at - position.recordedAt) : null,
      }
    })
    return { hits, total: matches.length, onMap: onMap.length }
  }, [athletes, athleteQuery, findable, raceOf])
  const athleteOverlay = useMemo<AthleteOverlayData | null>(() => {
    if (trackedAthletes.length === 0) return null
    return {
      type: 'FeatureCollection',
      features: trackedAthletes.map((athlete) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [athlete.longitude, athlete.latitude] },
        properties: {
          id: athlete.id,
          color: athlete.color,
          stale: athlete.stale,
          selected: athlete.id === pickedAthlete || spotlight?.has(athlete.id) === true,
          dim: spotlight !== null && !spotlight.has(athlete.id),
        },
      })),
    }
  }, [trackedAthletes, pickedAthlete, spotlight])
  // What an athlete's card shows, while they have a position.
  const athleteCardOf = useCallback(
    (id: string | null) => {
      const athlete = id ? athletes?.roster.find((each) => each.id === id) : undefined
      const position = athlete ? athletes?.positions.get(athlete.id) : undefined
      if (!athlete || !position || !athletes) return null
      const race = raceOf(athlete.routeId)
      return {
        athlete,
        position,
        race: race.name ? { name: race.name, color: race.color } : null,
        stale: athletes.at - position.recordedAt > athletes.staleAfterMs,
      }
    },
    [athletes, raceOf],
  )
  // The athlete whose card is open on the Mapbox map.
  const picked = useMemo(() => athleteCardOf(pickedAthlete), [athleteCardOf, pickedAthlete])
  const hovered = hoveredAthlete && hoveredAthlete !== pickedAthlete ? trackedAthletes.find((athlete) => athlete.id === hoveredAthlete) : undefined
  // The dots with a card or name label over them: they jump to a new fix with it, rather than glide away from it.
  const overlaid = useMemo(() => new Set([pickedAthlete, hoveredAthlete].filter((id): id is string => id !== null)), [pickedAthlete, hoveredAthlete])
  // The world opens the same card over its own dot.
  const worldAthleteCard = useCallback(
    (id: string, close: () => void) => {
      const card = athleteCardOf(id)
      return card ? <AthleteCard {...card} now={athletes?.at ?? 0} onClose={close} /> : null
    },
    [athleteCardOf, athletes],
  )

  useEffect(() => {
    if (!mapReady) return
    const map = mapRef.current?.getMap()
    if (!map) return
    if (syncAthleteOverlay(map, athleteOverlay, { glide: !reducedMotion, still: overlaid }) || !athleteOverlay) return

    const timeout = window.setTimeout(() => {
      setMapRevision((revision) => revision + 1)
    }, 250)
    return () => window.clearTimeout(timeout)
  }, [mapReady, mapRevision, mapStyle, athleteOverlay, reducedMotion, overlaid])

  useEffect(() => {
    if (!mapReady) return
    const map = mapRef.current?.getMap()
    if (map) fadeRouteOverlay(map, spotlight !== null)
  }, [mapReady, mapRevision, mapStyle, spotlight])

  // Location telemetry starts before SRT/video publishing. Keep every camera
  // with a fresh or pinned coordinate on the map; online state changes only
  // its treatment and whether it can open the player.
  const mappable = useMemo<Located<CourseMapCamera>[]>(
    () => locatedCameras(cameras),
    [cameras],
  )

  // While the viewer's own connection is down, no camera is live (see `connectionStatus`).
  const connected = connectionStatus === undefined

  // The course's box (every course's together): each line's once its GPX is in, the payload's bounds until then.
  const courseBox = useMemo<RouteBounds | null>(() => {
    if (!courseLines) return lineBox(parsed, route?.bounds)
    return joinBoxes(courseLines.map(({ course, line }) => lineBox(line, course.route.bounds)))
  }, [courseLines, parsed, route?.bounds])


  // What the map frames as it opens: every live camera with the course, or the located cameras with neither. Its key
  // says what is framed (the course, which cameras), so a camera moving or the course's line arriving doesn't reframe.
  const openingFrame = useMemo(() => {
    const live = connected ? mappable.filter((camera) => camera.online) : []
    const framed = courseBox || live.length > 0 ? live : mappable
    const box = boxAround(
      framed.map((camera) => camera.coordinate),
      courseBox,
    )
    if (!box) return null
    const identities = framed.map((camera) => camera.identity).sort()
    return { box, key: [courseKey, framed === live ? 'live' : 'located', ...identities].join('\n') }
  }, [connected, courseBox, courseKey, mappable])

  // Whether the map framed `box`: false with none, or no room to frame it yet
  // (a map not laid out, which Mapbox's fit ignores, or a hidden one, such as
  // the watch screen's phone Map tab behind another tab: Mapbox answers a
  // camera for an empty container, and that fit would latch). A box with no
  // area (one camera, no course) would zoom all the way in: the map eases to
  // it at a lone camera's zoom instead. Mapbox's fit would flatten the map and
  // turn it north up: it keeps the viewer's tilt (the 3D toggle) and bearing.
  const fitBox = useCallback((box: RouteBounds | null, duration: number, padding: (map: MapRef) => Padding = coursePadding) => {
    const map = mapRef.current
    if (!map || !box) return false
    const { clientWidth, clientHeight } = map.getContainer()
    if (!clientWidth || !clientHeight) return false
    if (box.west === box.east && box.south === box.north) {
      map.easeTo({ center: [box.west, box.south], zoom: LONE_CAMERA_ZOOM, duration, essential: false })
      return true
    }
    const bounds = lngLatBounds(box)
    const options = { padding: padding(map), duration, maxZoom: 14, pitch: map.getPitch(), bearing: map.getBearing() }
    if (!map.cameraForBounds(bounds, options)) return false
    map.fitBounds(bounds, options)
    return true
  }, [])
  const fitCourseRoute = useCallback((duration = 600) => fitBox(courseBox, duration), [courseBox, fitBox])

  // The opening frame holds from the map's opening until the viewer moves the
  // map (a gesture, the route and recentre buttons, focus panned clear of a
  // cover) or the follow rule moves it to a newly watched camera;
  // `openingFramed` is the frame's key as last framed, null until it has
  // framed since it opened. Held (`holdView`), the map stays put.
  const openingHolds = useRef(true)
  const openingFramed = useRef<string | null>(null)
  const held = useRef(holdView)
  // Following a host's map (`hostView.follow`), the view is theirs: the map's own moves hold too, and the viewer's own
  // move lets go (`letGo`).
  const follow = hostView?.follow ?? null
  const following = Boolean(follow?.on)
  const followed = useRef(follow)
  useLayoutEffect(() => {
    held.current = holdView || following
    followed.current = follow
  })
  const letGo = useCallback(() => {
    if (followed.current?.on) followed.current.onBreak()
  }, [])
  // What covers the map's foot (the swap card): how far up from the map's bottom edge, clear of it.
  const coverRoom = useCallback(() => {
    const cover = coveredBy?.()?.getBoundingClientRect()
    const stage = rootRef.current?.getBoundingClientRect()
    if (!cover || !stage || cover.top < stage.top + stage.height / 2) return 0
    return stage.bottom - cover.top + COVER_CLEARANCE
  }, [coveredBy])
  const fitOpening = useCallback(
    (duration: number) => {
      if (!openingFrame) return false
      const { box } = openingFrame
      if (!fitBox(box, duration, (map) => openingPadding(map, box, courseBox, coverRoom()))) return false
      openingFramed.current = openingFrame.key
      return true
    },
    [courseBox, coverRoom, fitBox, openingFrame],
  )

  // Frame as soon as the map can, trying again on every idle (a resize ends
  // in one) until it does, then again (a visible move) whenever what the frame
  // holds changes while it holds: the room connecting after the map loaded, a
  // camera going live. Without the first fit, the default selected/live camera
  // could pull the viewport away before the viewer has any way to recover it.
  useEffect(() => {
    if (!mapReady || !openingHolds.current || held.current || !openingFrame || openingFramed.current === openingFrame.key) return
    fitOpening(openingFramed.current === null ? 0 : 600)
  }, [fitOpening, mapReady, mapRevision, openingFrame])

  // The map opens afresh when it moves between the swap card and the canvas
  // (it turns interactive or still), or is shown again (the phone's Map tab):
  // it frames at its new size before it paints. The container already has
  // that size, so resize first. If the map isn't ready (a basemap is loading)
  // or the fit can't happen yet, the fit effect above does it on the next
  // idle. So it does when the map is out of the document as this runs: the
  // watch screen moving it to another place on the page (its MovableSlot)
  // appends it after this effect; the ResizeObserver above resizes it once
  // it's in. Another course opens afresh too, framed by the effect above.
  const seen = useRef({ interactive, shown, courseKey })
  useLayoutEffect(() => {
    const before = seen.current
    seen.current = { interactive, shown, courseKey }
    const flipped = before.interactive !== interactive
    const revealed = interactive && shown && !before.shown
    if (!flipped && !revealed && before.courseKey === courseKey) return
    openingHolds.current = true
    openingFramed.current = null
    if (!flipped && !revealed) return
    if (!mapReady || !mapRef.current?.getContainer().isConnected) return
    mapRef.current.resize()
    // A follower's map opens on the host's view, not a frame of its own.
    if (!followed.current?.on) fitOpening(0)
  }, [courseKey, fitOpening, interactive, mapReady, shown])

  // Fly to the watched camera when selection changes. `followSelection` is
  // the old viewer's "the viewer picked a camera" input to this rule. Held
  // (`holdView`), the map stays put, and letting go doesn't catch up. While
  // the opening frame holds, only the selection moving from one camera to
  // another follows: the first camera to be watched arriving (the room
  // connecting) or the cameras changing keep every live camera in view.
  const watchedBefore = useRef(selectedIdentity)
  useEffect(() => {
    const before = watchedBefore.current
    watchedBefore.current = selectedIdentity
    if (held.current) return
    if (openingHolds.current && (before === null || before === selectedIdentity)) return
    if (
      !shouldAutoFocusWatchedCamera({
        hasViewerOverride: followSelection,
        hasRouteBounds: Boolean(courseBox),
      })
    ) {
      return
    }
    const id = selectedIdentity
    if (!mapRef.current || !id) return
    const target = mappable.find((camera) => camera.identity === id)
    if (!target) return
    openingHolds.current = false
    mapRef.current.easeTo({
      center: [target.coordinate.longitude, target.coordinate.latitude],
      zoom: Math.max(mapRef.current.getZoom(), 11),
      duration: 600,
      essential: false,
    })
  }, [followSelection, selectedIdentity, mappable, courseBox])

  // Recenter = "show me everything": the course AND every located camera, so
  // cameras far from the course (e.g. a remote roving phone) stay visible.
  // One camera and no course: an ease to it.
  const recenter = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    openingHolds.current = false
    letGo()
    const box = boxAround(
      mappable.map((camera) => camera.coordinate),
      courseBox,
    )
    if (!box) return
    if (box.west === box.east && box.south === box.north) {
      map.easeTo({ center: [box.west, box.south], zoom: LONE_CAMERA_ZOOM, duration: 600, essential: false })
      return
    }
    map.fitBounds(lngLatBounds(box), { padding: coursePadding(map), duration: 600, maxZoom: 10, pitch: map.getPitch(), bearing: map.getBearing() })
  }, [courseBox, letGo, mappable])

  // One camera previews at a time. A preview is local to the map: it never
  // touches selection, so hovering a camera never eases the map.
  const cameraIds = useMemo(() => mappable.map((camera) => camera.identity), [mappable])
  const { expanded, modeOf, change } = useExpandedCamera(cameraIds)

  // Opening a camera ('full') goes to `onWatch` with the sphere's frame, read
  // before anything moves. The surface's player is the player: the sphere
  // tucks back into a circle behind it.
  const handleModeChange = useCallback<ChangeCameraMode>(
    (identity, next, onlyFrom) => {
      if (next !== 'full') {
        change(identity, next, onlyFrom)
        return
      }
      onWatch(identity, cameraFrame(rootRef.current, identity))
      change(identity, 'orb')
    },
    [change, onWatch],
  )

  // The world over the map, once it shows (its scene built off the main thread; until then the Mapbox map stands in, with
  // its build under way). A pin opens as a sphere does (`onWatch` with its frame).
  const [worldReady, setWorldReady] = useState(false)
  const worldMounted = world !== null
  useEffect(() => {
    if (!worldMounted) setWorldReady(false)
  }, [worldMounted])
  const worldShown = worldMounted && worldReady
  // A world downloading, or here with its scene still building, is on its way: the Mapbox map isn't what the viewer will see.
  const worldOnItsWay = worldDownloading || (worldMounted && !worldReady)
  const loaded = !mapboxToken || worldShown || (mapReady && !worldOnItsWay)
  useEffect(() => {
    onLoadedChange?.(loaded)
  }, [loaded, onLoadedChange])

  // A map that turns into a still picture (the swap card) puts its preview away.
  useEffect(() => {
    if (expanded && !interactive) change(expanded.id, 'orb')
  }, [change, expanded, interactive])

  // A search's finds come into view once the typing pauses: one athlete close up, a few framed together, and many leave
  // the view be. The world flies to a single one. Picking one from the list shows them and opens their card.
  const [worldFlight, setWorldFlight] = useState<{ id: string; select: boolean; key: number } | null>(null)
  const settling = useRef<number | undefined>(undefined)
  const latestAthletes = useRef(trackedAthletes)
  latestAthletes.current = trackedAthletes
  const spotlightKey = spotlight ? [...spotlight].sort().join() : ''
  const searchText = athleteQuery?.trim() ?? ''
  useEffect(() => {
    if (!spotlight || spotlight.size > SEARCH_FRAMES_UP_TO) return
    const timer = window.setTimeout(() => {
      settling.current = undefined
      const found = latestAthletes.current.filter((athlete) => spotlight.has(athlete.id))
      if (found.length === 0) return
      if (worldShown) {
        if (found.length === 1) setWorldFlight((last) => ({ id: found[0].id, select: false, key: (last?.key ?? 0) + 1 }))
        return
      }
      const map = mapRef.current
      if (!map) return
      // The viewer's view now: the opening frame no longer holds, and a host's map lets go.
      openingHolds.current = false
      letGo()
      const duration = reducedMotion ? 0 : 800
      if (found.length === 1) {
        map.easeTo({ center: [found[0].longitude, found[0].latitude], zoom: Math.max(map.getZoom(), SEARCH_ZOOM), duration, essential: false })
        return
      }
      fitBox(
        {
          west: Math.min(...found.map((athlete) => athlete.longitude)),
          east: Math.max(...found.map((athlete) => athlete.longitude)),
          south: Math.min(...found.map((athlete) => athlete.latitude)),
          north: Math.max(...found.map((athlete) => athlete.latitude)),
        },
        duration,
      )
    }, SEARCH_SETTLE_MS)
    settling.current = timer
    return () => window.clearTimeout(timer)
    // Keyed by what was typed and the athletes found: each keystroke restarts the pause, and a new read of their
    // positions doesn't move the view again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, spotlightKey, worldShown])
  const pickAthlete = useCallback(
    (id: string) => {
      // The pick is where the view goes: a pause still to come mustn't move it to the search's finds after it.
      window.clearTimeout(settling.current)
      if (worldShown) {
        setWorldFlight((last) => ({ id, select: true, key: (last?.key ?? 0) + 1 }))
        return
      }
      setPickedAthlete(id)
      const athlete = latestAthletes.current.find((each) => each.id === id)
      const map = mapRef.current
      if (!athlete || !map) return
      openingHolds.current = false
      letGo()
      map.easeTo({ center: [athlete.longitude, athlete.latitude], zoom: Math.max(map.getZoom(), SEARCH_ZOOM), duration: reducedMotion ? 0 : 800, essential: false })
    },
    [letGo, reducedMotion, worldShown],
  )
  // Showing an athlete asked from outside (`athleteFocus`): once the world on its way is on screen (or, without one, the
  // Mapbox map) and they have a dot there, as a pick from the search.
  const [pendingFocus, setPendingFocus] = useState<{ id: string; key: number; until: number } | null>(null)
  const focusKey = athleteFocus?.key
  useEffect(() => {
    if (!athleteFocus) return
    setPendingFocus({ id: athleteFocus.id, key: athleteFocus.key, until: Date.now() + ATHLETE_FOCUS_WAIT_MS })
    // Each key once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey])
  const worldComing = worldBuilding !== undefined || worldDownloading || (worldMounted && !worldReady)
  useEffect(() => {
    if (!pendingFocus) return
    const left = pendingFocus.until - Date.now()
    if (left <= 0) {
      setPendingFocus(null)
      return
    }
    const ready = !worldComing && (worldShown || mapReady) && trackedAthletes.some((athlete) => athlete.id === pendingFocus.id)
    if (ready) {
      setPendingFocus(null)
      pickAthlete(pendingFocus.id)
      return
    }
    // Not yet: look again when time's up (a dot or the world arriving re-runs this sooner).
    const timer = window.setTimeout(() => setPendingFocus((current) => (current && current.key === pendingFocus.key ? { ...current } : current)), left)
    return () => window.clearTimeout(timer)
  }, [pendingFocus, worldComing, worldShown, mapReady, trackedAthletes, pickAthlete])

  const searching = trackedAthletes.length > 0 && interactive && presentation === 'map'
  const athleteSearch = (compact: boolean) =>
    searching ? (
      <AthleteSearch
        query={athleteQuery}
        onQueryChange={setAthleteQuery}
        hits={searchList.hits}
        total={searchList.total}
        onMap={searchList.onMap}
        onPick={pickAthlete}
        compact={compact}
      />
    ) : null
  // The world is on screen as the full map and shown: it draws only then (the swap card's picture covers it, a hidden
  // tab hides it), and hears its keys only then and while interactive.
  const worldOnScreen = presentation === 'map' && shown
  // The Mapbox map out of sight (under the world, under the swap card's picture, behind another tab) holds its drawing
  // once its style has loaded, and draws afresh as it shows again. Its cleanup runs before the reveal's refit above, so
  // the map comes back at its new size and frame.
  const mapboxHidden = worldShown || presentation === 'card' || !shown
  useLayoutEffect(() => {
    const map = mapRef.current?.getMap()
    if (!mapboxHidden || !mapReady || !map) return
    return holdRepaints(map)
  }, [mapReady, mapboxHidden])

  // A host's map (`hostView`) on the Mapbox map while it is the live map on screen: interactive, the full map, shown, and
  // under no world (over a world still building it is). A live host's offers its view to `source`, read
  // when the sender asks. A follower's eases to the host's, and the opening frame no longer holds: when following ends,
  // the map stays where the host left it.
  const mapboxLive = interactive && presentation === 'map' && shown && !worldShown
  const mapCreated = mapRevision > 0
  const viewSource = hostView?.source ?? null
  // The athlete card open on the host's map goes with its view, so their followers see it too.
  const pickedNow = useRef(pickedAthlete)
  useLayoutEffect(() => {
    pickedNow.current = pickedAthlete
  })
  useEffect(() => {
    if (!viewSource || !mapboxLive || !mapCreated) return
    const read = () => {
      const map = mapRef.current?.getMap()
      const pose = map ? readMapboxView(map) : null
      const open = pickedNow.current
      return pose && open ? { ...pose, athlete: open } : pose
    }
    viewSource.set(read, basemap)
    return () => {
      if (viewSource.read === read) viewSource.set(null)
    }
  }, [viewSource, mapboxLive, mapCreated, basemap])
  const followStore = follow?.store ?? null
  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!following || !followStore || !mapboxLive || !mapCreated || !map) return
    openingHolds.current = false
    return followOnMapbox(map, followStore, { reducedMotion })
  }, [following, followStore, mapboxLive, mapCreated, reducedMotion])
  // The athlete whose card the host has open has theirs open here too while following on the Mapbox map (no ease: the
  // map follows the host's); the host closing it closes it. Once the viewer lets go, what's open is theirs.
  const hostAthlete = following && mapboxLive ? (follow?.athlete ?? null) : undefined
  useEffect(() => {
    if (hostAthlete !== undefined) setPickedAthlete(hostAthlete)
  }, [hostAthlete])
  // Over a world still building the Mapbox map is the live map, so the world only follows (in place once it shows).
  const worldHostView = useMemo(() => (worldShown || !hostView ? hostView : { follow: hostView.follow }), [hostView, worldShown])

  const stateOf = (camera: CourseMapCamera) =>
    !connected ? { live: false, status: connectionStatus } : camera.online ? { live: true } : { live: false, status: camera.status ?? 'Offline' }

  // The card's picture: each course's line (its colour with several, stacked as on the map), the payload's bounds until
  // the lines arrive, and a dot per located camera.
  const cardCourses = useMemo<CourseCardCourse[]>(() => {
    const lineOf = (line: ParsedGpx | null) => line?.points.map((point) => [point.longitude, point.latitude] as const) ?? []
    if (!courseLines) return [{ line: lineOf(parsed) }]
    return [...courseLines].sort((a, b) => a.order - b.order).map(({ course, line }) => ({ line: lineOf(line), color: course.color }))
  }, [courseLines, parsed])
  const boundsBox = useMemo(
    () => (courses ? joinBoxes(courses.map((course) => (isValidRouteBounds(course.route.bounds) ? course.route.bounds : null))) : isValidRouteBounds(route?.bounds) ? route.bounds : null),
    [courses, route?.bounds],
  )
  const courseCorners = useMemo(
    () =>
      boundsBox
        ? ([
            [boundsBox.west, boundsBox.south],
            [boundsBox.east, boundsBox.north],
          ] as const)
        : [],
    [boundsBox],
  )
  const cardDots = useMemo<CourseCardDot[]>(
    () =>
      mappable.map((camera) => ({
        identity: camera.identity,
        coordinate: camera.coordinate,
        live: connected && camera.online,
        selected: camera.identity === selectedIdentity,
      })),
    [connected, mappable, selectedIdentity],
  )

  // A camera taking focus under what covers the map is panned clear of it: upward when the cover sits in the lower half
  // (a preview grows up from the fix, so it clears too), downward otherwise.
  const revealFocused = useCallback(
    (event: ReactFocusEvent<HTMLDivElement>) => {
      const target = event.target
      const cover = coveredBy?.()
      const map = mapRef.current
      // A follower's map moves only with the host's.
      if (followed.current?.on) return
      if (!cover || !map || !(target instanceof HTMLElement) || !target.hasAttribute('data-camera-sphere')) return
      // Only the Mapbox map's own spheres: a play world pin over the hidden map isn't on it.
      if (!map.getContainer().contains(target)) return
      const camera = target.getBoundingClientRect()
      const over = cover.getBoundingClientRect()
      if (camera.right <= over.left || camera.left >= over.right || camera.bottom <= over.top || camera.top >= over.bottom) return
      const stage = rootRef.current?.getBoundingClientRect()
      const below = !stage || over.top + over.height / 2 >= stage.top + stage.height / 2
      const offset = below ? camera.bottom - over.top + COVER_CLEARANCE : camera.top - over.bottom - COVER_CLEARANCE
      openingHolds.current = false
      map.panBy([0, offset], { duration: 300, essential: false })
    },
    [coveredBy],
  )

  // The swap card's map is a picture: hidden from assistive tech and pointers,
  // so the card's own button is all they meet.
  const stillPicture = interactive ? {} : { 'aria-hidden': true, inert: '' }

  if (!mapboxToken) {
    return (
      <div className={cn('lv-map-empty', className)} {...stillPicture}>
        <div className="lv-map-empty-card">
          <p className="lv-map-empty-eyebrow">Map mode</p>
          <h3 className="lv-map-empty-title">Map unavailable</h3>
          <p className="lv-map-empty-body">
            The map isn’t available right now. You can still watch the cameras.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={rootRef}
      className={cn('lv-map', className)}
      data-route-overlay={routeOverlayReady ? 'ready' : 'pending'}
      data-presentation={presentation}
      data-world={worldShown || undefined}
      onFocus={coveredBy ? revealFocused : undefined}
      {...stillPicture}
    >
      <MapGL
        ref={mapRef}
        initialViewState={{
          longitude: route?.bounds
            ? (route.bounds.east + route.bounds.west) / 2
            : 30.6,
          latitude: route?.bounds
            ? (route.bounds.north + route.bounds.south) / 2
            : -29.85,
          zoom: 8.5,
          pitch: is3D ? 55 : 0,
        }}
        mapboxAccessToken={mapboxToken}
        mapStyle={mapStyle}
        style={{ width: '100%', height: '100%' }}
        attributionControl={false}
        {...(logoPosition ? { logoPosition } : {})}
        {...(interactive ? {} : STILL_MAP)}
        onLoad={handleMapLoad}
        onStyleData={applyLightPreset}
        onIdle={handleMapIdle}
        onMoveStart={(moveEvent) => {
          // The viewer's own gesture: the view is theirs now, and lets go of a host's map.
          if (!byViewer(moveEvent.originalEvent)) return
          openingHolds.current = false
          letGo()
        }}
        onMouseMove={(mapEvent) => {
          const map = mapRef.current?.getMap()
          const athlete = map && athleteOverlay ? athleteAt(map, mapEvent.point) : null
          setHoveredAthlete(athlete)
          const canvas = map?.getCanvas()
          if (canvas && (athlete || canvas.style.cursor === 'pointer')) canvas.style.cursor = athlete ? 'pointer' : ''
        }}
        onMouseOut={() => setHoveredAthlete(null)}
        onClick={(mapEvent) => {
          // Marker clicks bubble through the map; only a tap on the map itself puts a preview away.
          if ((mapEvent.originalEvent.target as Element | null)?.closest?.('.cs-sphere, .lv-athlete-card')) return
          // A tap on an athlete's dot opens their card; anywhere else puts it away.
          const map = mapRef.current?.getMap()
          const athlete = map && mapEvent.point ? athleteAt(map, mapEvent.point) : null
          setPickedAthlete(athlete)
          if (athlete) return
          if (expanded?.mode === 'peek') change(expanded.id, 'orb', ['peek'])
        }}
      >
        {mappable.map((cam) => {
          const mode = modeOf(cam.identity)
          const initials = <SphereInitials name={cam.name} />
          const { live, status } = stateOf(cam)
          return (
            <Marker
              key={cam.identity}
              longitude={cam.coordinate.longitude}
              latitude={cam.coordinate.latitude}
              anchor="bottom"
              style={{ zIndex: mode === 'orb' ? 1 : 2 }}
            >
              <CameraSphere
                id={cam.identity}
                name={cam.name}
                video={cam.online ? renderVideo(cam, initials) : initials}
                mode={mode}
                onModeChange={handleModeChange}
                live={live}
                status={status}
                freshness={cam.freshness}
                selected={selectedIdentity === cam.identity}
                featured={calledIdentity === cam.identity}
                disabled={!interactive || !cam.online}
                size="sm"
              />
            </Marker>
          )
        })}
        {hovered && interactive && presentation === 'map' && (
          <Marker longitude={hovered.longitude} latitude={hovered.latitude} anchor="bottom" offset={[0, -12]} style={{ zIndex: 3, pointerEvents: 'none' }}>
            <span className="lv-athlete-name">{hovered.name}</span>
          </Marker>
        )}
        {picked && interactive && presentation === 'map' && (
          <Marker
            longitude={picked.position.longitude}
            latitude={picked.position.latitude}
            anchor="bottom"
            offset={[0, -12]}
            style={{ zIndex: 3 }}
          >
            <AthleteCard
              athlete={picked.athlete}
              position={picked.position}
              race={picked.race}
              stale={picked.stale}
              now={athletes?.at ?? 0}
              onClose={closeAthlete}
            />
          </Marker>
        )}
        {attribution === 'compact' && <AttributionControl compact position={logoPosition ?? 'bottom-left'} />}
        {children}
      </MapGL>

      {world && (
        <Suspense fallback={null}>
          <CourseMapWorld
            eventId={eventId}
            route={route}
            cameras={cameras}
            worldElements={worldElements}
            trackedAthletes={trackedAthletes}
            athleteCard={worldAthleteCard}
            athleteMatches={spotlight}
            athleteFlight={worldFlight}
            search={athleteSearch(true)}
            selectedIdentity={selectedIdentity}
            calledIdentity={calledIdentity}
            onWatch={onWatch}
            interactive={interactive}
            worldControls={worldControls}
            active={interactive && worldOnScreen}
            paused={!worldOnScreen}
            card={presentation === 'card'}
            connectionStatus={connectionStatus}
            renderVideo={renderVideo}
            terrain={world.terrain}
            gpx={world.gpx}
            courses={world.courses}
            onBasemapChange={(next) => onBasemapChange?.(next)}
            onUnavailable={world.onUnavailable}
            onReady={() => setWorldReady(true)}
            hostView={worldHostView}
          />
        </Suspense>
      )}

      {/* A ready world is the card's picture itself; until then (or without one) the courses' lines. */}
      {presentation === 'card' && !worldShown && <CourseCard courses={cardCourses} bounds={courseCorners} dots={cardDots} />}

      {interactive && presentation === 'map' && courses && <CourseLegend courses={courses} />}

      {/* The page's control for a host's map, top centre, as the world has it (`.cm-world__host`). */}
      {interactive && presentation === 'map' && !worldShown && hostView?.overlay && <div className="lv-map-host">{hostView.overlay}</div>}

      {interactive && !worldShown && (worldBuilding !== undefined || worldMounted) && (
        <p className="lv-map-world-building" role="status">
          <MountainSnow size={14} strokeWidth={2} aria-hidden="true" />
          {typeof worldBuilding === 'number' ? `Building the world · ${Math.round(worldBuilding * 100)}%` : 'Building the world'}
        </p>
      )}
      {interactive && !worldShown && (
        <CourseMapControls
          search={athleteSearch(false)}
          basemap={basemap}
          is3D={is3D}
          hasRoute={Boolean(courseBox)}
          onBasemapChange={(next) => {
            setBasemap(next)
            onBasemapChange?.(next)
          }}
          world={onSelectWorld ? { building: worldBuilding !== undefined || worldMounted, problem: worldProblem, onSelect: onSelectWorld } : undefined}
          onDimensionChange={(next) => {
            letGo()
            setIs3D(next)
          }}
          onFitRoute={() => {
            openingHolds.current = false
            letGo()
            fitCourseRoute()
          }}
          onRecenter={recenter}
        />
      )}
    </div>
  )
}
